import React, { useContext, useEffect, useMemo } from 'react';
import { Skeleton, useSnackbar } from 'fronton-react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { AxiosError } from 'axios';
import { getCookie } from 'utils/cookie.helper';
import { gaSubmitDoorProject } from 'ga-events/ga-submit-door-project';
import { Snackbar, SNACKBARS_DICTIONARY, TSnackbarDictionaryKeys } from 'pages/common/components/Snackbar';
import { measurementServiceIds } from 'pages/common/consts/services';
import { Inputs } from '../../types';
import { useMeasurement } from '../../../common/hooks/use-measurement';
import { MeasurementBanner } from '../../components/MeasurementBanner/measurement-banner';
import {
  catalogueLevels,
  CUSTOMER_ID_COOKIE_NAME,
  UNAUTHORIZED_CUSTOMER_ID_COOKIE_NAME,
  userProjectConfigurationCookieNames,
  formNames,
  projectUpdatedCustomEventNames,
} from '../../const';
import { DoorModelParams } from '../../components/DoorModelParams/door-model-params';
import { ForWetRoomsParams } from '../../components/ForWetRoomsParams/for-wet-rooms-params';
import { ColourParams } from '../../components/ColourParams/colour-params';
import { DataContext } from '../../../../entries/common/widgets/project-configuration/context';
import { getCreateConfigurationBody } from './helpers/get-create-configuration-body';
import { getEditConfigurationBody } from './helpers/get-edit-configuration-body';
import { isKazakhstanRegion } from '../../helpers/is-kazakhstan-region';
import { getDefaultParams } from './helpers/get-default-params';
import { getProjectUpdatedCustomEventPayload } from './helpers/get-project-updated-custom-event-payload';
import { submitForm } from './helpers/submit-form';
import { Footer } from '../../components/Footer/footer';
import {
  FormStyled,
  Container,
  SkeletonContainer,
  FooterContainer,
  MeasurementBannerWrapper,
} from './styles';

export const InteriorDoorsForm = () => {
  const data = useContext(DataContext);
  const { addSnack } = useSnackbar();

  const methods = useForm<Inputs>();
  const defaultValues = useMemo(() => getDefaultParams(data?.projectConfigurationParameters), [
    data?.projectConfigurationParameters,
  ]);

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const { api } = data!;

  const onSubmit: SubmitHandler<Inputs> = (inputsData: Inputs) => {
    gaSubmitDoorProject({
      catalogueLevel: data?.catalogueLevel ?? catalogueLevels.SECOND,
      buttonText: data?.catalogueLevel === catalogueLevels.THIRD ? 'Применить' : 'Выбрать тип двери',
      params: inputsData,
    });
    const authorizedCustomerId = getCookie(CUSTOMER_ID_COOKIE_NAME);
    /* кука должна быть не пустой всегда,
         т.к. идентификатор яндекс метрики присваивается через куку при заходе на сайт
       */
    const unauthorizedCustomerId = getCookie(UNAUTHORIZED_CUSTOMER_ID_COOKIE_NAME) as string;
    const isAuthorized = Boolean(authorizedCustomerId);
    const customerId = isAuthorized ? (authorizedCustomerId as string) : unauthorizedCustomerId;
    const isKazakhstan = isKazakhstanRegion(data!.regionId);
    const createConfigurationBody = getCreateConfigurationBody(
      inputsData,
      isKazakhstan,
      customerId,
      isAuthorized
    );
    const editConfigurationBody = getEditConfigurationBody(inputsData);
    const customEventPayload = getProjectUpdatedCustomEventPayload(inputsData);
    submitForm({
      api,
      projectConfigurationCookieName: userProjectConfigurationCookieNames.INTERIOR_DOORS,
      editConfigurationBody,
      createConfigurationBody,
      projectUpdatedCustomEventName: projectUpdatedCustomEventNames.INTERIOR_DOORS,
      projectUpdatedCustomEventPayload: customEventPayload,
    }).catch(e => {
      const status = (e as AxiosError).response?.status || '';
      addSnack(
        String(status).startsWith('4')
          ? SNACKBARS_DICTIONARY[TSnackbarDictionaryKeys.SAVE_DOOR_PROJECT_CLIENT_ERROR]
          : SNACKBARS_DICTIONARY[TSnackbarDictionaryKeys.SAVE_PROJECT_SERVER_ERROR]
      );
    });
  };

  const { isLoading, isAvailable } = useMeasurement({
    serviceId: measurementServiceIds.INTERIOR_DOORS,
    regionId: data?.regionId,
    api: data?.api,
  });

  return (
    <FormProvider {...methods}>
      <Container>
        <FormStyled id={formNames.INTERIOR_DOORS} onSubmit={methods.handleSubmit(onSubmit)}>
          {/* Здесь были расположены параметры длины, ширины и глубины, но в MVP их решили убрать.
            https://github.lmru.tech/adeo/lmru--triffle--calculator/pull/74 - вот PR, где они еще были
            https://github.lmru.tech/adeo/lmru--triffle--calculator/pull/78 - вот PR, где они были убраны
            */}
          <DoorModelParams />
          <ForWetRoomsParams />
          <ColourParams />
        </FormStyled>
        {isLoading && (
          <SkeletonContainer>
            <Skeleton variant="control" width="100%" height="100%" roundingSize="l" />
          </SkeletonContainer>
        )}
        {isAvailable && (
          <MeasurementBannerWrapper>
            <MeasurementBanner />
          </MeasurementBannerWrapper>
        )}
        <Snackbar horizontalPosition="end" verticalPosition="bottom" />
      </Container>
      <FooterContainer>
        <Footer />
      </FooterContainer>
    </FormProvider>
  );
};
